/**
 * Selected Store Module
 * Manages all information about the selected store location (sucursal)
 * This is the single source of truth for selected store data and business logic
 * Compatible with Vue 2
 */

import {
  isRestaurantClosed,
  getWorkingHours
} from "@/composables/useRestaurantHours";
import { calculateStoreStatus as calculateStoreStatusHelper } from "@/utils/storeStatusHelper";
import moment from "moment-timezone";

export const selectedStore = {
  namespaced: true,
  state: {
    // Selected location data (sucursal)
    selectedLocation: null
  },
  getters: {
    /**
     * Gets the currently selected store location
     * @returns {Object|null} Selected store location object or null
     */
    getSelectedLocation: (state) => state.selectedLocation,

    /**
     * Gets the timezone for the selected store location
     * @returns {string} Store timezone or "UTC" as fallback
     */
    getStoreTimezone: (state, getters, rootState, rootGetters) => {
      const selectedLocation = state.selectedLocation;
      if (selectedLocation && selectedLocation.timezone) {
        return selectedLocation.timezone;
      }
      return rootGetters["store/getStoreTimezone"] || "UTC";
    },

    /**
     * Gets the store status based on current time and dining options timings
     * @returns {string} 'open' | 'closed' | 'schedule_only'
     * - 'open': Current time is within at least one dining option's hours
     * - 'closed': Current time is not within any dining option's hours
     * - 'schedule_only': Current time is not within any dining option's hours BUT at least one dining option supports scheduling
     */
    getStoreStatus: (state, getters, rootState, rootGetters) => {
      const selectedLocation = state.selectedLocation;
      if (!selectedLocation) {
        return "open"; // Can't determine, assume open
      }

      const timezone = getters.getStoreTimezone;
      const allDiningOptions =
        rootGetters["store/availableDiningOptions"] || [];

      // Use centralized helper function (single source of truth)
      return calculateStoreStatusHelper(
        selectedLocation,
        allDiningOptions,
        timezone
      );
    },

    /**
     * Checks if the store is closed
     * Store is considered closed when NONE of its dining options match current time/day
     * @returns {boolean} True if store is closed, false otherwise
     */
    isStoreClosed: (state, getters) => {
      const status = getters.getStoreStatus;
      return status === "closed";
    },

    /**
     * Checks if the store supports scheduled orders
     * Returns true if at least one dining option has scheduling enabled
     * (regardless of whether it's currently available, as scheduled orders are for future times)
     * @returns {boolean} True if store supports scheduled orders
     */
    isScheduleEnabled: (state, getters, rootState, rootGetters) => {
      const selectedLocation = state.selectedLocation;
      if (!selectedLocation) {
        return false;
      }

      const allDiningOptions =
        rootGetters["store/availableDiningOptions"] || [];

      // Check if at least one dining option has scheduling enabled
      // We check all options, not just available ones, because scheduled orders are for future times
      return allDiningOptions.some((option) => {
        return option.is_scheduling_enabled === true;
      });
    },

    /**
     * Determines if an order can be scheduled for the current store
     * Returns true when store status is 'schedule_only' (closed but allows scheduling)
     * or when store is open and at least one dining option supports scheduling
     * @returns {boolean} True if order can be scheduled
     */
    canScheduleOrder: (state, getters) => {
      const status = getters.getStoreStatus;

      // If store is in 'schedule_only' mode, orders can definitely be scheduled
      if (status === "schedule_only") {
        return true;
      }

      // If store is open, check if any dining option supports scheduling
      if (status === "open") {
        return getters.isScheduleEnabled;
      }

      // If store is closed and doesn't support scheduling, orders cannot be scheduled
      return false;
    },

    /**
     * Helper getter to check if dining option timing is open
     * @param {Object} diningOption - Dining option with timings
     * @param {string} timezone - Store timezone
     * @returns {boolean} True if dining option timing is currently open
     */
    isDiningOptionTimingOpen: () => {
      return (diningOption, timezone) => {
        if (
          !diningOption ||
          !diningOption.timings ||
          diningOption.timings.length === 0
        ) {
          return false;
        }

        const now = moment().tz(timezone);
        const todayDay = now.isoWeekday(); // Returns 1-7 (Monday=1, Sunday=7)

        // Find timings for today
        const timingsForToday = diningOption.timings.filter(
          (timing) => timing.day_of_week === todayDay
        );

        if (timingsForToday.length === 0) {
          return false; // No timings for today, closed
        }

        // Check if all timings for today are closed
        const allClosed = timingsForToday.every(
          (timing) => timing.is_closed === true
        );
        if (allClosed) {
          return false; // All timings are closed
        }

        // Check if any timing for today is open (is_closed is false AND current time is within hours)
        const hasOpenTiming = timingsForToday.some((timing) => {
          // If is_closed is true, skip this timing
          if (timing.is_closed === true) {
            return false;
          }

          // Check if current time is within opening_time and closing_time
          const today = now.clone().startOf("day");
          const openTime = today
            .clone()
            .add(moment.duration(timing.opening_time));
          const closeTime = today
            .clone()
            .add(moment.duration(timing.closing_time));

          // Handle case where closing time is next day (e.g., 23:00 - 02:00)
          if (closeTime.isBefore(openTime) || closeTime.isSame(openTime)) {
            // Closing time is next day or same as opening
            return now.isSameOrAfter(openTime) || now.isBefore(closeTime);
          }

          // Normal case: opening time < closing time
          return now.isSameOrAfter(openTime) && now.isBefore(closeTime);
        });

        return hasOpenTiming;
      };
    },

    /**
     * Checks if a dining option is currently available (within its hours)
     * @param {Object} diningOption - Dining option to check
     * @param {string} timezone - Store timezone
     * @param {Object} storeData - Store data
     * @returns {boolean} True if dining option is currently available
     */
    isDiningOptionAvailable: (state, getters) => {
      return (diningOption, timezone, storeData) => {
        if (!diningOption) {
          return false;
        }

        // First check if dining option has timings - use new logic
        if (diningOption.timings && diningOption.timings.length > 0) {
          return getters.isDiningOptionTimingOpen(diningOption, timezone);
        }

        // Fallback to old logic if no timings
        const isClosed = isRestaurantClosed(diningOption, timezone, storeData);
        return !isClosed;
      };
    },

    /**
     * Gets filtered dining options that are currently available
     * Filters out dining options that:
     * - Are currently closed AND don't support scheduling
     * - If a dining option doesn't support scheduling and is outside its hours, it's filtered out
     * @returns {Array} Filtered array of available dining options
     */
    getAvailableDiningOptions: (state, getters, rootState, rootGetters) => {
      const selectedLocation = state.selectedLocation;
      if (!selectedLocation) {
        return [];
      }

      const timezone = getters.getStoreTimezone;
      const allDiningOptionsRaw =
        rootGetters["store/availableDiningOptions"] || [];

      // Ensure allDiningOptions is always an array
      const allDiningOptions = Array.isArray(allDiningOptionsRaw)
        ? allDiningOptionsRaw
        : [];

      if (allDiningOptions.length === 0) {
        return [];
      }

      return allDiningOptions.filter((option) => {
        // Check if dining option is currently available (within its hours)
        const isAvailable = getters.isDiningOptionAvailable(
          option,
          timezone,
          selectedLocation
        );

        // If it's available, include it
        if (isAvailable) {
          return true;
        }

        // If it's not available but supports scheduling, include it (for future orders)
        if (option.is_scheduling_enabled === true) {
          return true;
        }

        // If it's not available and doesn't support scheduling, filter it out
        return false;
      });
    },

    /**
     * Gets formatted working hours string for display
     * Uses the current dining option if available, otherwise falls back to store hours
     * @returns {string} Formatted hours string or "Not Available"
     */
    getStoreWorkingHours: (state, getters, rootState, rootGetters) => {
      const selectedLocation = state.selectedLocation;
      if (!selectedLocation) {
        return "Not Available";
      }

      const timezone = getters.getStoreTimezone;
      const diningOptionId = rootGetters["store/dining_option"];
      const allDiningOptions =
        rootGetters["store/availableDiningOptions"] || [];

      // Try to get hours from current dining option
      if (diningOptionId && allDiningOptions.length > 0) {
        const diningOption = allDiningOptions.find(
          (option) => option.id === diningOptionId
        );

        if (diningOption) {
          const hours = getWorkingHours(
            diningOption,
            timezone,
            selectedLocation
          );
          if (hours && hours !== "Not Available") {
            return hours;
          }
        }
      }

      // Fallback to store basic hours
      if (selectedLocation.start_time && selectedLocation.close_time) {
        return getWorkingHours(null, timezone, selectedLocation);
      }

      return "Not Available";
    }
  },
  mutations: {
    /**
     * Sets the selected location
     * @param {Object} state - Vuex state
     * @param {Object} location - Location object to set
     */
    SET_SELECTED_LOCATION(state, location) {
      state.selectedLocation = location;
    }
  },
  actions: {
    /**
     * Sets selected location
     * This is the single source of truth for selected location
     * Note: app/setSelectedLocation will call this, so we avoid circular calls
     * @param {Object} context - Vuex context
     * @param {Object} location - Location object to set
     */
    setSelectedLocation({ commit }, location) {
      if (!location) {
        console.warn(
          "selectedStore/setSelectedLocation: location is null or undefined"
        );
        return;
      }

      // Set in this module (single source of truth)
      commit("SET_SELECTED_LOCATION", location);
    },

    /**
     * Gets the next open day information for the store
     * Finds the closest day that has:
     * - At least one timing with is_closed === false, OR
     * - If all timings have is_closed === true, then at least one dining option with is_scheduling_enabled === true
     * @param {Object} context - Vuex context
     * @returns {Object|null} Object with { dayName, dayDate, hours, openingTime } or null if no open day found
     */
    getNextOpenDay({ getters, rootGetters }) {
      const selectedLocation = getters.getSelectedLocation;
      if (!selectedLocation) {
        return null;
      }

      const timezone = getters.getStoreTimezone;
      const allDiningOptions =
        rootGetters["store/availableDiningOptions"] || [];
      const now = moment().tz(timezone);

      // If no dining options, check store basic hours
      if (allDiningOptions.length === 0) {
        if (selectedLocation.start_time && selectedLocation.close_time) {
          // For basic store hours, check if it's closed today
          const isClosed = isRestaurantClosed(null, timezone, selectedLocation);
          if (!isClosed) {
            // Store is open today, return today's info
            const openTime = moment.tz(
              selectedLocation.start_time,
              "HH:mm:ss",
              timezone
            );
            const closeTime = moment.tz(
              selectedLocation.close_time,
              "HH:mm:ss",
              timezone
            );
            return {
              isToday: true,
              isTomorrow: false,
              dayName: now.format("dddd"),
              dayDate: now.format("MMMM D"),
              hours: `${openTime.format("h:mm A")} - ${closeTime.format(
                "h:mm A"
              )}`,
              openingTime: openTime.format("h:mm A")
            };
          }
          // If closed today, tomorrow will be the next open day (assuming same hours)
          const tomorrow = now.clone().add(1, "day");
          const openTime = moment.tz(
            selectedLocation.start_time,
            "HH:mm:ss",
            timezone
          );
          const closeTime = moment.tz(
            selectedLocation.close_time,
            "HH:mm:ss",
            timezone
          );
          return {
            isToday: false,
            isTomorrow: true,
            dayName: tomorrow.format("dddd"),
            dayDate: tomorrow.format("MMMM D"),
            hours: `${openTime.format("h:mm A")} - ${closeTime.format(
              "h:mm A"
            )}`,
            openingTime: openTime.format("h:mm A")
          };
        }
        return null;
      }

      // Check next 7 days
      for (let dayOffset = 0; dayOffset < 7; dayOffset++) {
        const checkDate = now.clone().add(dayOffset, "days");
        const checkDay = checkDate.isoWeekday(); // 1-7 (Monday=1, Sunday=7)
        const dayName = checkDate.format("dddd");
        const dayDate = checkDate.format("MMMM D");

        // Collect all timings for this day across all dining options
        let hasOpenTiming = false;
        let hasScheduleOption = false;
        const openTimingsForDay = [];
        const closedTimingsForDay = [];

        for (const option of allDiningOptions) {
          // Check if option supports scheduling (regardless of timings)
          if (option.is_scheduling_enabled === true) {
            hasScheduleOption = true;
          }

          if (!option.timings || option.timings.length === 0) {
            continue;
          }

          // Find all timings for this day (both open and closed)
          const allTimingsForDay = option.timings.filter(
            (timing) => timing.day_of_week === checkDay
          );

          if (allTimingsForDay.length === 0) {
            continue;
          }

          // Separate open and closed timings
          const openTimings = allTimingsForDay.filter(
            (timing) => timing.is_closed === false
          );
          const closedTimings = allTimingsForDay.filter(
            (timing) => timing.is_closed === true
          );

          // If there are open timings, format them
          if (openTimings.length > 0) {
            hasOpenTiming = true;
            const hoursList = openTimings.map((timing) => {
              const dayStart = checkDate.clone().startOf("day");
              const openTime = dayStart
                .clone()
                .add(moment.duration(timing.opening_time));
              const closeTime = dayStart
                .clone()
                .add(moment.duration(timing.closing_time));
              return `${openTime.format("h:mm A")} - ${closeTime.format(
                "h:mm A"
              )}`;
            });

            const optionName = option.name || option.type || "";
            if (optionName) {
              openTimingsForDay.push(`${optionName}: ${hoursList.join(", ")}`);
            } else {
              openTimingsForDay.push(...hoursList);
            }
          } else if (closedTimings.length > 0) {
            // Store closed timings info for potential scheduling display
            closedTimingsForDay.push({
              optionName: option.name || option.type || "",
              hasScheduling: option.is_scheduling_enabled === true
            });
          }
        }

        // Day qualifies if:
        // 1. Has at least one open timing (is_closed === false), OR
        // 2. All timings are closed (is_closed === true) but has at least one option with is_scheduling_enabled === true
        const allTimingsClosed =
          !hasOpenTiming && closedTimingsForDay.length > 0;
        const qualifiesForScheduling = allTimingsClosed && hasScheduleOption;

        if (hasOpenTiming) {
          // Day has open timings, return it
          // Get the earliest opening time for display
          let earliestOpenTime = null;
          for (const option of allDiningOptions) {
            if (!option.timings || option.timings.length === 0) {
              continue;
            }
            const openTimings = option.timings.filter(
              (timing) =>
                timing.day_of_week === checkDay && timing.is_closed === false
            );
            for (const timing of openTimings) {
              const dayStart = checkDate.clone().startOf("day");
              const openTime = dayStart
                .clone()
                .add(moment.duration(timing.opening_time));
              if (!earliestOpenTime || openTime.isBefore(earliestOpenTime)) {
                earliestOpenTime = openTime;
              }
            }
          }

          return {
            isToday: dayOffset === 0,
            isTomorrow: dayOffset === 1,
            dayName: dayName,
            dayDate: dayDate,
            hours: openTimingsForDay.join(" | "),
            openingTime: earliestOpenTime
              ? earliestOpenTime.format("h:mm A")
              : null
          };
        } else if (qualifiesForScheduling) {
          // All timings are closed but scheduling is available
          // Show that scheduling is available for this day
          const scheduleOptions = closedTimingsForDay
            .filter((item) => item.hasScheduling)
            .map((item) => item.optionName)
            .filter((name) => name)
            .join(", ");

          return {
            isToday: dayOffset === 0,
            isTomorrow: dayOffset === 1,
            dayName: dayName,
            dayDate: dayDate,
            hours: scheduleOptions
              ? `Schedule available (${scheduleOptions})`
              : "Schedule available",
            openingTime: null
          };
        }
      }

      // No open day found in the next 7 days
      return null;
    }
  }
};
